import React, {
  memo, useCallback, useEffect, useMemo, useState,
} from 'react';
import { TablePagination } from '@material-ui/core';
import { CheckCircleOutline } from '@material-ui/icons';
import { useDispatch, useSelector } from 'react-redux';
import SaveActionBtnGrp from 'components/kea/settings/SaveActionBtnGrp';
import BlockingProgress from 'components/kea/BlockingProgress';
import {
  actions,
  selectAllSynonyms,
  selectFilteredSortedSynonyms,
  selectHasChanges,
  selectHasInvalidSynonyms,
  loadSynonyms,
  saveSynonyms,
} from 'slices/SynonymsSlice';
import { useShellAppContext } from 'features/shell/context/useShellAppContext';
import SearchFeaturePageShell from 'components/shell/SearchFeaturePageShell';
import { useLocalizedMessage } from 'util/useLocalizedMessage';
import { ManualSynonymsTable } from './components/ManualSynonyms/ManualSynonymsTable';
import { TableToolbar } from './components/ManualSynonyms/TableToolbar';
import { TypeExplanation } from './components/ManualSynonyms/TypeExplanation';
import ImportSynonymsDialog from './components/ImportSynonymsDialog';
import {
  buildSynonymExportRows,
  downloadSynonymsCsv,
} from './export/synonymCsvExport';
import styles from './Synonyms.module.scss';

const DEFAULT_ROWS_PER_PAGE = 25;

const Synonyms = memo(() => {
  const dispatch = useDispatch();
  const localizeMessage = useLocalizedMessage();
  const { capabilities = {}, compatibility } = useShellAppContext();
  const { currentShop } = useSelector((state) => state.app);
  const {
    isFetching, isSaving, searchTerm, sortBy, sortOrder,
  } = useSelector((state) => state.synonyms);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [importOpen, setImportOpen] = useState(false);
  const [importReport, setImportReport] = useState(null);
  const [validationAttempted, setValidationAttempted] = useState(false);
  const deferredReindexEnabled = compatibility?.catalogUpdates?.deferred
    ?? capabilities.deferredReindexV1 === true;
  const loadError = useSelector((state) => state.synonyms.loadError);
  const allSynonyms = useSelector(selectAllSynonyms);
  const synonyms = useSelector(selectFilteredSortedSynonyms);
  const hasChanges = useSelector(selectHasChanges);
  const hasInvalidSynonyms = useSelector(selectHasInvalidSynonyms);
  const saveInProgress = isSaving && (importOpen || !hasInvalidSynonyms);
  useEffect(() => {
    if (!hasChanges) setValidationAttempted(false);
  }, [hasChanges, currentShop]);
  useEffect(() => {
    if (!currentShop) return;
    dispatch(loadSynonyms(currentShop));
  }, [dispatch, currentShop]);

  useEffect(() => {
    setPage(0);
  }, [currentShop, searchTerm, sortBy, sortOrder]);

  useEffect(() => {
    setImportOpen(false);
    setImportReport(null);
  }, [currentShop]);

  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(synonyms.length / rowsPerPage) - 1);
    setPage(current => Math.min(current, lastPage));
  }, [rowsPerPage, synonyms.length]);

  const visibleSynonyms = useMemo(() => {
    const start = page * rowsPerPage;
    return synonyms.slice(start, start + rowsPerPage);
  }, [page, rowsPerPage, synonyms]);

  const handleUpdate = useCallback((id, changes) => {
    dispatch(actions.updateSynonym({ id, changes: { _isModified: true, ...changes } }));
  }, [dispatch]);

  const handleDelete = useCallback((id) => {
    dispatch(actions.deleteSynonym(id));
  }, [dispatch]);

  const handleAddSynonym = useCallback(() => {
    setValidationAttempted(false);
    dispatch(actions.setSearchTerm(''));
    dispatch(actions.addSynonym());
    setPage(0);
  }, [dispatch]);

  const handleSearchChange = useCallback((event) => {
    dispatch(actions.setSearchTerm(event.target.value));
  }, [dispatch]);

  const handleSort = useCallback((payload) => {
    dispatch(actions.setSort(payload));
  }, [dispatch]);

  const handleSave = () => {
    setValidationAttempted(true);
    return dispatch(saveSynonyms({
      shopId: currentShop,
      deferredReindex: deferredReindexEnabled,
    }));
  };

  const handleDiscard = () => {
    setValidationAttempted(false);
    dispatch(actions.discardChanges());
  };

  const handleImport = useCallback(async (result) => {
    const sourceStats = result.sourceStats || {};
    const saveResult = await dispatch(saveSynonyms({
      shopId: currentShop,
      deferredReindex: deferredReindexEnabled,
      synonyms: result.synonyms,
    }));
    if (saveResult?.meta?.requestStatus !== 'fulfilled') return false;

    setImportReport({
      ...result.stats,
      skipped: result.stats.skipped
        + (sourceStats.invalidRows || 0)
        + (sourceStats.duplicates || 0)
        + (sourceStats.conflicts || 0),
    });
    setPage(0);
    return true;
  }, [currentShop, deferredReindexEnabled, dispatch]);

  const exportRowCount = useMemo(
    () => buildSynonymExportRows(allSynonyms).length,
    [allSynonyms],
  );
  const handleExport = useCallback(() => {
    downloadSynonymsCsv(allSynonyms);
  }, [allSynonyms]);

  if (!currentShop) return null;

  return (
    <SearchFeaturePageShell
      pageId="customizationSynonyms"
      description={localizeMessage('search.synonyms.section.hint')}
      isLoading={isFetching}
      error={loadError && !isFetching ? loadError : null}
      actions={(
        <SaveActionBtnGrp
          disabled={isSaving}
          buttons={[
            {
              text: localizeMessage('common.save'),
              action: handleSave,
              color: 'primary',
              disabled: !hasChanges,
              testId: 'synonyms-save',
            },
            {
              text: localizeMessage('common.discard'),
              action: handleDiscard,
              color: 'secondary',
              disabled: !hasChanges,
              testId: 'synonyms-discard',
            },
          ]}
        />
      )}
    >
      <fieldset className={styles.editorControls} disabled={isSaving} aria-busy={isSaving}>
        <section className={styles.surface} data-testid="synonyms-surface">
          <TableToolbar
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
            onAddSynonym={handleAddSynonym}
            onImport={() => setImportOpen(true)}
            onExport={handleExport}
            canExport={exportRowCount > 0}
            showSearch={allSynonyms.length > 5 || Boolean(searchTerm)}
          />

          {importReport && (
            <div className={styles.importReport} role="status" data-testid="synonyms-import-report">
              <CheckCircleOutline />
              <span>
                <strong>{localizeMessage('search.synonyms.import.reportTitle')}</strong>
                {localizeMessage('search.synonyms.import.reportBody', importReport)}
              </span>
            </div>
          )}

          <div className={styles.tableWrap}>
            <ManualSynonymsTable
              synonyms={visibleSynonyms}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onUpdate={handleUpdate}
              onDelete={handleDelete}
              onSort={handleSort}
              showValidationErrors={validationAttempted}
            />
            {synonyms.length > DEFAULT_ROWS_PER_PAGE && (
              <TablePagination
                component="div"
                count={synonyms.length}
                page={page}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[25, 50, 100]}
                onPageChange={(event, nextPage) => setPage(nextPage)}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(Number(event.target.value));
                  setPage(0);
                }}
                data-testid="synonyms-pagination"
              />
            )}
          </div>

          <TypeExplanation />

          <ImportSynonymsDialog
            key={currentShop}
            open={importOpen}
            onClose={() => setImportOpen(false)}
            onApply={handleImport}
            existingSynonyms={allSynonyms}
          />

        </section>
      </fieldset>
      <BlockingProgress
        open={saveInProgress}
        label={localizeMessage('search.synonyms.saving')}
        testId="synonyms-saving"
      />
    </SearchFeaturePageShell>
  );
});

export default Synonyms;
